import { CSSProperties, PureComponent, ReactNode, createRef } from 'react';
import { MdClose } from 'react-icons/md';
import { Virtuoso } from 'react-virtuoso';

import Downshift, { ControllerStateAndHelpers, StateChangeOptions } from 'downshift';
import isEqual from 'fast-deep-equal';
import { isEmpty, uniqBy } from 'lodash/fp';
import toString from 'lodash/toString';
import { defaultBaseSortFn, matchSorter, rankings } from 'match-sorter';
import removeAccents from 'remove-accents';
import { DropdownOption, DropdownOptionGroup, DropdownValue } from 'types';

import _ from '../../locale';
import theme, { styled } from '../../theme';
import { COMPACT_BUTTON_SIZE } from '../../theme/theme-config';
import { Color, Size, Status, matchSize } from '../../utility/common-props-helpers';
import FlexRow from '../FlexRow';
import BaseInput from '../Input';
import SvgIcon from '../SvgIcon';
import TruncatedTextWithTooltip from '../TruncatedTextWithTooltip';
import { checkIsEnoughSpaceAboveForMenu, createOptionsFromGranularity } from './utils';

/**
 * Consts
 */
const MULTISELECT_HOTKEY = 'shiftKey';
const MIN_TOP_OPTION_QUERY_LENGTH = 2;

/**
 * The menu is rendered at `theme.zIndex.dropdownMenus`, so tooltips raised from inside the
 * dropdown have to match it or they get painted underneath the open menu.
 */
const truncatedLabelTooltipStyle: CSSProperties = {
    zIndex: theme.zIndex.dropdownMenus,
};

/**
 * Renders a string label as truncated-with-tooltip; anything else (already a ReactNode supplied
 * by the consumer) is passed through untouched.
 */
const renderTruncatableLabel = (label: ReactNode, shouldSuppressTooltip = false) =>
    typeof label === 'string' ? (
        <TruncatedTextWithTooltip
            text={label}
            tooltipStyle={truncatedLabelTooltipStyle}
            shouldSuppressTooltip={shouldSuppressTooltip}
        />
    ) : (
        label
    );

/**
 * Types
 */
export type DropdownInnerState<T = DropdownValue> = {
    isOpen: boolean;
    inputValue: string | null;
    visibleOptions: DropdownOption<T>[];
    topOptions: DropdownOption<T>[];
};

export type OptionGetters = Required<
    Pick<
        Props,
        | 'optionValueGetter'
        | 'optionLabelGetter'
        | 'optionDisabledGetter'
        | 'optionHiddenGetter'
        | 'optionSearchTermGetter'
    >
>;

export type SingleEventValue = {
    target: {
        name?: string;
        value: DropdownValue | null;
    };
};

export type ArrayEventValue = {
    target: {
        name?: string;
        value: DropdownValue[];
    };
};

type SortOptionsOrder = 'asc';

const isArrayEventValue = (
    ev: SingleEventValue | ArrayEventValue | undefined
): ev is ArrayEventValue => ev != null && Array.isArray(ev.target.value);

const isOptionGroup = (option): option is DropdownOptionGroup =>
    typeof option === 'object' && 'options' in option;

const defaultProps = {
    size: 'md',
    color: 'tertiary',
    optionValueGetter: (o) => o,
    optionLabelGetter: (o) => o,
    optionDisabledGetter: (o) => false,
    optionHiddenGetter: (o) => false,
};

export type Props<T = DropdownValue> = {
    className?: string;
    id?: string;
    dataTestid?: string;
    style?: CSSProperties;
    size?: Size;
    color?: Color;
    status?: Status;
    autoFocus?: boolean;
    disabled?: boolean;
    isOpen?: boolean;
    isSearchable?: boolean;
    isCollapsible?: boolean;
    visibleRowsCountWhenCollapsed?: number;
    isClearable?: boolean;
    name?: string;
    placeholder?: string;
    options: (DropdownOption | DropdownOptionGroup)[];
    optionsLabel?: string;
    topOptionsLabel?: string;
    multiselectLabel?: string;
    sortOptionsOrder?: SortOptionsOrder;
    valueMode: 'single' | 'singleArray' | 'multiArray';
    value: DropdownValue | DropdownValue[] | null;
    optionValueGetter?: (option: DropdownOption) => DropdownValue;
    optionLabelGetter?: (option: DropdownOption) => string;
    optionDisabledGetter?: (option: DropdownOption) => boolean;
    optionHiddenGetter?: (option: DropdownOption) => boolean;
    optionSearchTermGetter?: (option: DropdownOption) => string;
    optionHeightGetter?: (option: DropdownOption) => number | undefined;
    renderMenuHeader?: (innerState: DropdownInnerState) => ReactNode;
    renderOption?: (option: DropdownOption, optionsGetters: OptionGetters) => ReactNode;
    renderSelectedOption?: (option: DropdownOption) => ReactNode;
    renderTopOption?: (option: DropdownOption, optionsGetters: OptionGetters) => ReactNode;
    menuStyle?: CSSProperties;
    inputFieldContainerStyle?: CSSProperties;
    innerInputFieldStyle?: CSSProperties;
    selectedOptionStyle?: CSSProperties;
    getSelectedOptionContainerStyle?: (option: DropdownOption) => CSSProperties | undefined;
    topOptionSearchTermGetter?: (option: DropdownOption, query: string) => string | string[];
    topOptionGroupKeyGetter?: (option: DropdownOption) => string;
    topOptionTieBreaker?: (a: DropdownOption, b: DropdownOption, query: string) => number;
    onInputChange?: (inputValue: string, context?: { fromItemSelection?: boolean }) => void;
    onChange?: (ev: SingleEventValue | ArrayEventValue, innerState: DropdownInnerState) => void;
    onSelect?: (ev: SingleEventValue | ArrayEventValue, innerState: DropdownInnerState) => void;
    onRemove?: (ev: ArrayEventValue, innerState: DropdownInnerState) => void;
    onMultiSelect?: (ev: ArrayEventValue, innerState: DropdownInnerState) => void;
    onClear?: (ev: SingleEventValue | ArrayEventValue, innerState: DropdownInnerState) => void;
    checkIsOptionVisible?: ({
        inputSearchTerm,
        option,
        optionSearchTerm,
    }: {
        inputSearchTerm: string;
        option: DropdownOption;
        optionSearchTerm: string;
    }) => void;
    onOpen?: () => void;
    onClose?: () => void;
    inputFieldStyle?: object;
    isSecondary?: boolean;
    label?: string;
};

type State = {
    topOptions: DropdownOption[];
    visibleOptions: DropdownOption[];
    menuPosition: 'bottom' | 'top';
    multiItemSelectionStartIndex: number | null;
    isExpanded?: boolean;
    canCollapse?: boolean;
    lastVisibleRowTop?: number | null;
    lastVisibleRowBottom?: number | null;
    overlayTopPx?: number | null;
    overlayHeightPx?: number | null;
};

/**
 * Utils
 */
const ITEM_HEIGHT = 49;
const COMPACT_ITEM_HEIGHT = 44;
const XS_ITEM_HEIGHT = 28;
const XS_ITEM_WIDTH = 232;
const MAX_HEIGHT = 300;
const getPlaceholderInputWidth = (placeholder?: string) =>
    `${Math.round((placeholder?.length ?? 20) * 0.95)}ch`;

const getMenuMetrics = (size: Size | undefined) => {
    if (size === 'xs') {
        return {
            itemHeight: XS_ITEM_HEIGHT,
            menuWidthPx: XS_ITEM_WIDTH,
            menuPaddingYPx: 8,
            menuOffsetYPx: 4,
        };
    }
    return {
        itemHeight: size === 'sm' ? COMPACT_BUTTON_SIZE : ITEM_HEIGHT,
        menuWidthPx: null as number | null,
        menuPaddingYPx: null as number | null,
        menuOffsetYPx: null as number | null,
    };
};

/**
 * Styles
 */
const RootContainer = styled.div<{ size?: Size }>(({ theme, size }) => ({
    position: 'relative',
    fontFamily: theme.typography.fontFamily,
    fontWeight: theme.typography.fontWeight.roman,
    fontSize: matchSize({ xs: '1.4rem', sm: '1.4rem', md: '1.6rem' })(size),
    outline: 'none',
}));

const InputContainer = styled.div({
    position: 'relative',
});

const InputFieldContainer = styled(BaseInput, {
    shouldForwardProp: (prop) =>
        ![
            'status',
            'overlayTopPx',
            'overlayHeightPx',
            'showCollapsedOverlay',
            'clipStickyOverflow',
            'size',
        ].includes(prop),
})<
    Pick<
        Props,
        | 'id'
        | 'status'
        | 'disabled'
        | 'color'
        | 'isSearchable'
        | 'valueMode'
        | 'inputFieldStyle'
        | 'isSecondary'
    > & {
        overlayTopPx?: number | null;
        overlayHeightPx?: number | null;
        showCollapsedOverlay?: boolean;
        clipStickyOverflow?: boolean;
    }
>(
    ({
        theme,
        status,
        disabled,
        color,
        isSearchable,
        valueMode,
        inputFieldStyle,
        isSecondary,
        overlayTopPx,
        overlayHeightPx,
        showCollapsedOverlay,
        clipStickyOverflow,
        size,
    }) => ({
        outline: 'none',
        position: 'relative',
        width: '100%',
        overflow: clipStickyOverflow ? 'clip' : undefined,
        padding: matchSize({
            xs: '4px 10px 4px 12px',
            sm: '5px 24px 5px 15px',
            md: isSecondary ? '3px 40px 3px 15px' : '5px 40px 5px 15px',
        })(size),
        cursor: 'text',
        color: isSecondary ? theme.color[color + '1'] : theme.color[color + 'Text'],
        border: isSecondary
            ? `2px solid ${theme.color[color + '1']}`
            : theme.border[`${color}`] || 'unset',
        backgroundColor: isSecondary ? theme.color.white : theme.color[color + '1'],
        ...(valueMode === 'multiArray' && {
            height: 'auto',
        }),
        ...(!isSearchable && {
            cursor: 'pointer',
        }),
        ...(disabled && {
            opacity: 0.7,
            pointerEvents: 'none',
        }),
        ...(status && {
            border: '1px solid ' + theme.color[status],
        }),
        ...inputFieldStyle,
        ':hover': isSecondary
            ? {
                  backgroundColor: theme.color.base2,
                  color: theme.color.white,
                  button: { color: theme.color.white },
              }
            : {},
        ...(showCollapsedOverlay &&
            overlayTopPx != null && {
                '&::after': {
                    content: '""',
                    position: 'absolute',
                    left: 0,
                    right: 0,
                    top: `${overlayTopPx}px`,
                    height: `${overlayHeightPx ?? 60}px`,
                    pointerEvents: 'auto',
                    zIndex: 2,
                    background:
                        'linear-gradient(0deg, #E8EAE9 2.49%, rgba(234, 236, 235, 0.80) 56.19%, rgba(240, 242, 241, 0.00) 108.47%)',
                },
            }),
    })
);

const InputField = styled.input<Pick<Props, 'size' | 'placeholder'>>(
    {
        background: 'transparent',
        border: 'none',
        padding: 0,
        font: 'inherit',
        cursor: 'inherit',
        minWidth: 0,
        maxWidth: '100%',
        textOverflow: 'ellipsis',
    },
    ({ theme, size, placeholder }) => ({
        caretColor: theme.color.primary1,
        '::placeholder': {
            color: theme.color.gray40,
        },
        height: matchSize({ xs: 28, sm: 34, md: 40 })(size),
        width: getPlaceholderInputWidth(placeholder),
    })
);

const SELECTED_OPTION_MARGIN_RIGHT = 10;

const SelectedOptionContainer = styled.div<
    Pick<Props, 'valueMode' | 'disabled' | 'size'> & { isPlaceholder?: boolean; size?: Size }
>(
    {
        display: 'inline-block',
        verticalAlign: 'top',
        padding: '0 8px',
    },
    ({ theme, valueMode, disabled, isPlaceholder, size = 'md' }) => ({
        ...(valueMode === 'multiArray' && {
            backgroundColor: theme.color.white,
            borderRadius: theme.borderRadius.base,
        }),
        ...(isPlaceholder && {
            color: theme.color.gray40,
        }),
        ...(disabled && {
            color: theme.color.disabled,
        }),
        minHeight: matchSize({ xs: 28, sm: 28, md: 30 })(size),
        margin: matchSize({
            xs: `2px ${SELECTED_OPTION_MARGIN_RIGHT}px 2px 0`,
            sm: `2px ${SELECTED_OPTION_MARGIN_RIGHT}px 2px 0`,
            md: `5px ${SELECTED_OPTION_MARGIN_RIGHT}px 5px 0`,
        })(size),
        ...(valueMode === 'single' && {
            boxSizing: 'border-box' as const,
            maxWidth: `calc(100% - ${SELECTED_OPTION_MARGIN_RIGHT}px)`,
        }),
    })
);

const SelectedOption = styled.div({
    width: '100%',
});

const SelectedOptionRemoveButton = styled.button({
    cursor: 'pointer',
    border: 'none',
    background: 'transparent',
    padding: 0,
    marginLeft: 4,
});

const StyledClearIcon = styled(MdClose)(({ theme }) => ({
    verticalAlign: 'top',
    height: 30,
    marginRight: -2,
    color: theme.color.primary1,
    ':hover, :focus': {
        color: theme.color.red,
    },
    ':active': {
        color: theme.color.primary3,
    },
}));

const ActionButton = styled.button<Pick<Props, 'disabled' | 'color' | 'isSecondary'>>(
    ({ theme, disabled, color, isSecondary }) => ({
        color: isSecondary ? theme.color.baseIcon : theme.color[color + 'Icon'],
        position: 'absolute',
        padding: 0,
        top: 0,
        right: 14,
        height: '100%',
        backgroundColor: 'transparent',
        border: 'none',
        cursor: 'pointer',
        ...(disabled && {
            opacity: 0.7,
            pointerEvents: 'none',
        }),
    })
);

const CollapseButton = styled.button<{ isExpanded: boolean }>(({ theme, isExpanded }) => ({
    position: 'absolute',
    top: isExpanded ? 5 : 4,
    right: 4,
    height: 'auto',
    background: theme.color.white,
    border: 'none',
    cursor: 'pointer',
    padding: '0px 8px',
    borderRadius: theme.borderRadius.base,
    color: theme.color.green1,
    fontFamily: theme.typography.fontFamily,
    fontWeight: theme.typography.fontWeight.roman,
}));

const SelectedItemsWrapper = styled.div<{
    isCollapsed: boolean;
    maxHeightPx: number | null;
    canCollapse: boolean;
}>(({ isCollapsed, maxHeightPx, canCollapse }) => ({
    display: canCollapse ? 'block' : 'contents',
    position: 'relative',
    overflow: isCollapsed ? 'hidden' : 'visible',
    ...(isCollapsed && maxHeightPx != null
        ? {
              maxHeight: maxHeightPx + 'px',
          }
        : {}),
}));

const SearchRow = styled.div<{ isSticky: boolean }>(({ theme, isSticky }) => ({
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    ...(isSticky && {
        position: 'sticky',
        top: 0,
        zIndex: 1,
        background: theme.color.backgroundGray,
        paddingBottom: 5,
        '&::before': {
            content: '""',
            position: 'absolute',
            left: 0,
            right: 0,
            top: -30,
            bottom: 0,
            background: theme.color.backgroundGray,
            zIndex: -1,
            pointerEvents: 'none',
        },
        '&::after': {
            content: '""',
            position: 'absolute',
            left: 0,
            right: 0,
            bottom: 0,
            height: 1,
            background: theme.color.gray15,
        },
    }),
}));

const MenuContainer = styled.div<{
    isOnTop?: boolean;
    isOpen?: boolean;
    size?: Size;
}>(
    ({ theme }) => ({
        zIndex: theme.zIndex.dropdownMenus,
        position: 'absolute',
        width: '100%',
        overflow: 'hidden auto',
    }),
    ({ theme, isOnTop, isOpen, size }) => {
        const metrics = getMenuMetrics(size);
        return {
            margin: 0,
            background: theme.color.secondary1,
            boxShadow: theme.boxShadow.base1,
            ...(metrics.menuWidthPx != null ? { width: `${metrics.menuWidthPx}px` } : {}),
            ...(metrics.menuPaddingYPx != null
                ? {
                      paddingTop: `${metrics.menuPaddingYPx}px`,
                      paddingBottom: `${metrics.menuPaddingYPx}px`,
                  }
                : {}),
            ...(!isOpen ? { display: 'none' } : {}),
            ...(isOnTop
                ? {
                      bottom: '100%',
                      marginBottom:
                          metrics.menuOffsetYPx != null ? `${metrics.menuOffsetYPx}px` : undefined,
                      borderRadius: `${theme.borderRadius.base}px ${theme.borderRadius.base}px 0 0`,
                  }
                : {
                      top: '100%',
                      marginTop:
                          metrics.menuOffsetYPx != null ? `${metrics.menuOffsetYPx}px` : undefined,
                      borderRadius: `0 0 ${theme.borderRadius.base}px ${theme.borderRadius.base}px`,
                  }),
        };
    }
);

const TopOptionList = styled.ul({
    padding: 0,
});

const TopOptionLabel = styled.div<{ size?: Size }>(({ theme, size }) => ({
    margin: matchSize({ xs: '0 15px', sm: '0 15px', md: '0 30px 0 20px' })(size),
    padding: '14px 0',
    fontSize: matchSize({ xs: '1.1rem', sm: '1.1rem', md: '1.2rem' })(size),
    fontWeight: theme.typography.fontWeight.heavy,
    color: theme.color.gray40,
    textTransform: 'uppercase',
}));

const TopOptionFlexRow = styled(FlexRow)({
    borderBottom: '1px solid rgba(162,163,164, 0.3)',
});

const OptionListItem = styled.li<{
    isSelected?: boolean;
    isHighlighted?: boolean;
    isMultiSelectionHighlighted?: boolean;
    disabled?: boolean;
    size?: Size;
}>(
    ({ size }) => ({
        cursor: 'pointer',
        display: 'block',
        padding: '13px 20px',
        fontSize: matchSize({ xs: '1.4rem', sm: '1.4rem', md: 'inherit' })(size),
        minHeight: matchSize({ xs: XS_ITEM_HEIGHT, sm: COMPACT_ITEM_HEIGHT, md: ITEM_HEIGHT })(
            size
        ),
        textTransform: 'none',
        whiteSpace: 'pre-wrap',
        wordWrap: 'normal',
    }),
    ({ theme, isSelected, isHighlighted, isMultiSelectionHighlighted, disabled }) => ({
        color: theme.color.jetBlack,
        ...(isSelected && {
            fontWeight: theme.typography.fontWeight.medium,
            fontSize: 'inherit',
            color: theme.color.primary1,
        }),
        ...(isHighlighted && {
            background: theme.color.secondary2,
        }),
        ...(isMultiSelectionHighlighted && {
            background: theme.color.secondary2,
        }),
        ...(disabled && {
            color: theme.color.gray40,
            cursor: 'default',
        }),
    })
);

/**
 * Component
 */

/**
 @todo: disable search if there are no options
 @todo: dropdown empty value
 @todo: size: 'large' | 'base', needs to be implemented
 @todo: isSearchable: needs to be fixed for years dropdown
 @todo: closeMenuOnSelect: needs to be implemented
 @todo: required:  needs to be implemented
 **/

export class Dropdown extends PureComponent<Props, State> {
    static createOptionsFromGranularity = createOptionsFromGranularity;
    static defaultProps = defaultProps;

    listRef = createRef<typeof Virtuoso>();
    inputRef = createRef<HTMLInputElement>();
    menuRef = createRef<HTMLDivElement>();
    rootRef = createRef<HTMLDivElement>();
    selectedWrapperRef = createRef<HTMLDivElement>();

    mounted = false;

    state: State = {
        ...this.getVisibleOptions(this.props.options),
        menuPosition: 'bottom',
        multiItemSelectionStartIndex: null,
        isExpanded: false,
        canCollapse: false,
        lastVisibleRowTop: null,
        lastVisibleRowBottom: null,
    };

    constructor(props) {
        super(props);
        const { valueMode, value } = props;

        // check mode and value for errors
        if (value != null) {
            switch (valueMode) {
                case 'single':
                    if (Array.isArray(value)) {
                        throw Error("dropdown value shouldn't be an array in current valueMode");
                    }
                    break;
                case 'singleArray':
                case 'multiArray':
                    if (!Array.isArray(value)) {
                        throw Error('dropdown value should be an array in current valueMode');
                    }
                    break;

                default:
                    throw Error('dropdown has wrong valueMode');
            }
        }
    }

    safeRequestAnimationFrame = (callback: () => void) => {
        if (typeof window !== 'undefined' && window.requestAnimationFrame) {
            window.requestAnimationFrame(() => {
                if (this.mounted) callback();
            });
        } else {
            callback();
        }
    };

    componentDidMount(): void {
        this.mounted = true;
        window.addEventListener('scroll', this.handleScroll, true);
        window.addEventListener('resize', this.measureSelectedRows, true);
        this.updateMenuPosition();
        this.safeRequestAnimationFrame(() => {
            this.measureSelectedRows();
            setTimeout(() => {
                if (this.mounted) this.measureSelectedRows();
            }, 100);
        });
    }

    componentWillUnmount() {
        this.mounted = false;
        window.removeEventListener('scroll', this.handleScroll);
        window.removeEventListener('resize', this.measureSelectedRows, true);
    }

    componentDidUpdate(prevProps: Props, prevState: State) {
        const { options, optionValueGetter, value } = this.props;

        if (
            !isEqual(
                this.getVisibleOptions(prevProps.options).visibleOptions.map(optionValueGetter),
                this.getVisibleOptions(options).visibleOptions.map(optionValueGetter)
            )
        ) {
            // Always reset to show all options when props change
            // Don't use inputRef value as it may contain selected items display (e.g., ", , , ")
            // which would incorrectly filter out all options
            this.resetVisibleOptions();
        }

        this.updateMenuPosition();

        if (!isEqual(prevProps.value, value)) {
            this.safeRequestAnimationFrame(() => {
                this.measureSelectedRows();
            });
        }
    }

    isMulti = () => this.props.valueMode === 'multiArray';
    isSingleArray = () => this.props.valueMode === 'singleArray';

    isArrayValueMode = () => this.isMulti() || this.isSingleArray();

    handleScroll = () => this.updateMenuPosition();

    sortOptions(options) {
        const { optionSearchTermGetter = this.props.optionLabelGetter, sortOptionsOrder } =
            this.props;
        let sortingFunction;

        switch (sortOptionsOrder) {
            case 'asc':
            default:
                sortingFunction = (a, b) =>
                    optionSearchTermGetter(a).localeCompare(optionSearchTermGetter(b));
        }

        options.sort(sortingFunction);
    }

    // update menu position to be top or bottom
    updateMenuPosition = () => {
        if (!this.mounted) {
            return;
        }

        const node = this.rootRef.current as Element | null;
        if (!node) {
            return;
        }

        const rect = node.getBoundingClientRect();
        if (!rect) {
            return;
        }

        const menuPosition =
            rect.y + 350 > window.innerHeight &&
            checkIsEnoughSpaceAboveForMenu({
                triggerNode: node,
                triggerRect: rect,
                menuNode: this.menuRef.current,
            })
                ? 'top'
                : 'bottom';

        if (menuPosition !== this.state.menuPosition && this.mounted) {
            this.setState({ menuPosition });
        }
    };

    measureSelectedRows = () => {
        if (!this.mounted) return;
        const { isCollapsible, valueMode, visibleRowsCountWhenCollapsed = 3 } = this.props as Props;
        const {
            canCollapse: currentCanCollapse,
            isExpanded: currentIsExpanded,
            lastVisibleRowTop: currentLastVisibleRowTop,
            lastVisibleRowBottom: currentLastVisibleRowBottom,
            overlayTopPx: currentOverlayTopPx,
            overlayHeightPx: currentOverlayHeightPx,
        } = this.state;

        if (!(isCollapsible && valueMode === 'multiArray')) {
            if (currentCanCollapse || currentIsExpanded || currentLastVisibleRowTop != null) {
                this.setState({
                    canCollapse: false,
                    isExpanded: false,
                    lastVisibleRowTop: null,
                    lastVisibleRowBottom: null,
                });
            }
            return;
        }

        const wrapper = this.selectedWrapperRef.current;
        if (!wrapper) return;

        const chipNodes = Array.from(
            wrapper.querySelectorAll('[data-selected-chip]')
        ) as HTMLElement[];
        if (chipNodes.length === 0) {
            if (
                currentCanCollapse ||
                currentLastVisibleRowTop != null ||
                currentLastVisibleRowBottom != null
            ) {
                this.setState({
                    canCollapse: false,
                    isExpanded: false,
                    lastVisibleRowTop: null,
                    lastVisibleRowBottom: null,
                });
            }
            return;
        }

        const topSet: number[] = [];
        chipNodes.forEach((el) => {
            const top = el.offsetTop;
            if (!topSet.includes(top)) topSet.push(top);
        });
        topSet.sort((a, b) => a - b);

        const rowsCount = topSet.length;
        const canCollapse = rowsCount > visibleRowsCountWhenCollapsed;
        const lastVisibleRowIndex = visibleRowsCountWhenCollapsed - 1;
        const lastVisibleRowTop = canCollapse ? topSet[lastVisibleRowIndex] : null;
        let lastVisibleRowBottom: number | null = null;
        if (canCollapse && topSet.length >= visibleRowsCountWhenCollapsed) {
            const lastVisibleRowElements = chipNodes.filter(
                (el) => el.offsetTop === topSet[lastVisibleRowIndex]
            );
            if (!isEmpty(lastVisibleRowElements)) {
                lastVisibleRowBottom = Math.max(
                    ...lastVisibleRowElements.map((el) => el.offsetTop + el.offsetHeight)
                );
            }
        }
        const firstHiddenRowIndex = visibleRowsCountWhenCollapsed;
        let firstHiddenRowTop: number | null = null;
        let firstHiddenRowBottom: number | null = null;
        if (canCollapse && topSet.length >= visibleRowsCountWhenCollapsed + 1) {
            firstHiddenRowTop = topSet[firstHiddenRowIndex];
            const firstHiddenRowElements = chipNodes.filter(
                (el) => el.offsetTop === topSet[firstHiddenRowIndex]
            );
            if (!isEmpty(firstHiddenRowElements)) {
                firstHiddenRowBottom = Math.max(
                    ...firstHiddenRowElements.map((el) => el.offsetTop + el.offsetHeight)
                );
            }
        }
        const overlayTopPx =
            firstHiddenRowTop != null
                ? firstHiddenRowTop
                : lastVisibleRowBottom != null
                  ? lastVisibleRowBottom + 5
                  : null;
        const overlayHeightPx =
            firstHiddenRowBottom != null && firstHiddenRowTop != null
                ? firstHiddenRowBottom - firstHiddenRowTop + 10
                : null;

        if (
            canCollapse !== currentCanCollapse ||
            lastVisibleRowTop !== currentLastVisibleRowTop ||
            lastVisibleRowBottom !== currentLastVisibleRowBottom ||
            overlayTopPx !== currentOverlayTopPx ||
            overlayHeightPx !== currentOverlayHeightPx
        ) {
            const shouldResetExpanded = currentCanCollapse && !canCollapse;

            this.setState(
                {
                    canCollapse,
                    lastVisibleRowTop,
                    lastVisibleRowBottom,
                    overlayTopPx,
                    overlayHeightPx,
                    ...(shouldResetExpanded ? { isExpanded: false } : {}),
                },
                () => {
                    if (canCollapse && (overlayTopPx == null || overlayHeightPx == null)) {
                        this.safeRequestAnimationFrame(() => {
                            this.measureSelectedRows();
                        });
                    }
                }
            );
        }
    };

    /**
     * Works only with visible items in the list menu
     */
    scrollToItem = (highlightedIndex) => {
        if (this.listRef.current != null) {
            this.listRef.current.scrollToItem(highlightedIndex);
        }
    };

    defaultRenderOption = (option: DropdownOption, optionGetters: OptionGetters) =>
        option == null ? '' : optionGetters.optionLabelGetter(option);

    resetVisibleOptions() {
        this.setState({
            ...this.getVisibleOptions(this.props.options),
        });
    }

    updateVisibleOptions(inputValue) {
        this.setState({
            ...this.getVisibleOptions(this.props.options, inputValue),
        });
    }

    flattenOptionsProp(options: Props['options']): DropdownOption[] {
        const { sortOptionsOrder } = this.props;
        const flattenOptions = options.reduce((result: DropdownOption[], option) => {
            if (isOptionGroup(option)) {
                return result.concat(option.options);
            }
            return result.concat(option);
        }, []);
        if (sortOptionsOrder) {
            this.sortOptions(flattenOptions);
        }

        return flattenOptions;
    }

    checkIsOptionVisible = (inputValue: string, option: DropdownOption) => {
        const { optionSearchTermGetter = this.props.optionLabelGetter } = this.props;
        const inputSearchTerm = removeAccents(toString(inputValue).toLowerCase());
        const optionSearchTerm = removeAccents(
            toString(optionSearchTermGetter(option)).toLowerCase()
        );

        const isVisible = this.props.checkIsOptionVisible
            ? this.props.checkIsOptionVisible({ inputSearchTerm, option, optionSearchTerm })
            : optionSearchTerm.includes(inputSearchTerm);
        return isVisible;
    };

    getVisibleOptions(
        options: Props['options'],
        inputValue?: string | null
    ): {
        visibleOptions: DropdownOption[];
        topOptions: DropdownOption[];
    } {
        const {
            optionSearchTermGetter = this.props.optionLabelGetter,
            topOptionSearchTermGetter = optionSearchTermGetter,
            topOptionGroupKeyGetter,
            topOptionTieBreaker,
            sortOptionsOrder,
        } = this.props;
        const topOptionQuery = toString(inputValue).trim();
        // returns initial options on mount or on reset
        if (!topOptionQuery) {
            return {
                visibleOptions: this.flattenOptionsProp(options),
                topOptions: [],
            };
        }
        const checkIsOptionVisible = this.checkIsOptionVisible.bind(this, inputValue);

        const topOptions = [] as DropdownOption[];
        const visibleOptions = options.reduce((visibleOptions: DropdownOption[], optionOrGroup) => {
            if (isOptionGroup(optionOrGroup)) {
                const optionGroup = optionOrGroup;
                const visibleGroupOptions = optionGroup.options.filter(checkIsOptionVisible);
                if (topOptionQuery.length >= MIN_TOP_OPTION_QUERY_LENGTH) {
                    const rankedTopOptions = matchSorter(visibleGroupOptions, topOptionQuery, {
                        keys: [
                            {
                                threshold: rankings.CONTAINS,
                                key: (option) => topOptionSearchTermGetter(option, topOptionQuery),
                            },
                        ],
                        baseSort: (a, b) =>
                            topOptionTieBreaker?.(a.item, b.item, topOptionQuery) ||
                            defaultBaseSortFn(a, b),
                    });
                    const groupTopOptions = topOptionGroupKeyGetter
                        ? uniqBy(topOptionGroupKeyGetter, rankedTopOptions)
                        : rankedTopOptions.slice(0, 1);
                    topOptions.push(...groupTopOptions);
                }
                return visibleOptions.concat(visibleGroupOptions);
            }

            const option = optionOrGroup;
            if (checkIsOptionVisible(option)) {
                return visibleOptions.concat(option);
            }

            return visibleOptions;
        }, []);

        if (sortOptionsOrder) {
            this.sortOptions(visibleOptions);
        }

        return {
            visibleOptions,
            topOptions,
        };
    }

    getInnerState = (downshift: ControllerStateAndHelpers<DropdownOption>): DropdownInnerState => {
        const { visibleOptions, topOptions } = this.state;
        return {
            isOpen: downshift.isOpen,
            inputValue: downshift.inputValue,
            visibleOptions: visibleOptions,
            topOptions: topOptions,
        };
    };

    handleValueToLabel = (downshiftValue: DropdownOption | DropdownOption[] | null): string => {
        const { optionLabelGetter } = this.props;

        if (downshiftValue == null) {
            return '';
        }

        if (Array.isArray(downshiftValue)) {
            return downshiftValue.map(optionLabelGetter).join(', ');
        }

        return optionLabelGetter(downshiftValue);
    };

    getChangeEvent = (
        selectedOption: DropdownOption | null,
        downshift: ControllerStateAndHelpers<DropdownOption>
    ): SingleEventValue | ArrayEventValue | undefined => {
        const { valueMode, value, optionValueGetter } = this.props;

        const selectedOptionValue = selectedOption && optionValueGetter(selectedOption);

        let arrayValue: DropdownValue[];
        switch (valueMode) {
            case 'single':
                if (Array.isArray(value)) {
                    throw Error('wrong value type, expected primitive or null');
                }

                // check for no changes
                if (selectedOptionValue === value) {
                    return;
                }

                const ev: SingleEventValue = {
                    target: {
                        name: this.props.name,
                        value: selectedOptionValue,
                    },
                };
                return ev;

            case 'singleArray':
                if (!Array.isArray(value)) {
                    throw Error('wrong value type, expected array');
                }

                // check for no changes
                if (selectedOptionValue === value[0]) {
                    return;
                }

                if (selectedOptionValue == null) {
                    arrayValue = [];
                } else {
                    arrayValue = [selectedOptionValue];
                }
                break;

            case 'multiArray':
                if (!Array.isArray(value)) {
                    throw Error('wrong value type, expected array');
                }

                // check for no changes
                if (selectedOptionValue == null) {
                    arrayValue = [];
                } else if (value.includes(selectedOptionValue)) {
                    // remove existing value
                    arrayValue = value.filter((val) => val !== selectedOptionValue);
                } else {
                    // add new value
                    arrayValue = value.concat(selectedOptionValue);
                }
                break;

            default:
                throw Error('wrong valueMode');
        }

        const ev: ArrayEventValue = {
            target: {
                name: this.props.name,
                value: arrayValue,
            },
        };

        return ev;
    };

    handleItemSelect = (
        selectedOption: DropdownOption,
        downshift: ControllerStateAndHelpers<DropdownOption>
    ) => {
        const { onChange, onSelect } = this.props;

        if (this.isMulti() && event?.[MULTISELECT_HOTKEY]) {
            return;
        }

        const ev = this.getChangeEvent(selectedOption, downshift);
        if (ev != null) {
            const innerState = this.getInnerState(downshift);
            onChange?.(ev, innerState);
            onSelect?.(ev, innerState);
        }
    };

    handleClearButtonClick = (downshift: ControllerStateAndHelpers<DropdownOption>) => {
        const { onChange, onClear } = this.props;

        const ev = this.getChangeEvent(null, downshift);

        if (ev != null) {
            const innerState = this.getInnerState(downshift);
            onChange?.(ev, innerState);
            onClear?.(ev, innerState);
        }
    };

    handleRemoveButtonClick = (
        selectedOption: DropdownOption,
        downshift: ControllerStateAndHelpers<DropdownOption>
    ) => {
        const { onChange, onRemove } = this.props;

        const ev = this.getChangeEvent(selectedOption, downshift);

        if (isArrayEventValue(ev)) {
            const innerState = this.getInnerState(downshift);
            onChange?.(ev, innerState);
            onRemove?.(ev, innerState);
        }
    };

    getRemoveButtonProps = ({ onClick, item, ...props }: any = {}, downshift) => {
        return {
            onClick: (e) => {
                e.preventDefault();
                onClick?.(e);
                e.stopPropagation();
                this.handleRemoveButtonClick(item, downshift);
            },
            ...props,
        };
    };

    stateReducer = (
        state: ControllerStateAndHelpers<DropdownOption>,
        changes: StateChangeOptions<DropdownOption>
    ) => {
        switch (changes.type) {
            // when selected an item with a keyboard or mouse
            case Downshift.stateChangeTypes.keyDownEnter:
            case Downshift.stateChangeTypes.clickItem:
                // preserve open menu
                return {
                    ...changes,
                    inputValue: this.isMulti() ? state.inputValue : '',
                    isOpen: state.isOpen,
                    highlightedIndex: state.highlightedIndex,
                };

            // when changed a value prop
            case Downshift.stateChangeTypes.controlledPropUpdatedSelectedItem:
                if (this.isMulti()) {
                    // in multi selection preserve
                    return {
                        ...changes,
                        inputValue: state.inputValue,
                    };
                } else if (this.isSingleArray()) {
                    // in single array selection preserve
                    return {
                        ...changes,
                        inputValue: state.inputValue,
                        isOpen: false,
                    };
                } else {
                    // in single selection close dropdown on value change
                    return {
                        ...changes,
                        isOpen: false,
                    };
                }
        }

        // when dropdown is opened
        if (changes.hasOwnProperty('isOpen') && changes.isOpen) {
        }

        // when dropdown is closed
        if (changes.hasOwnProperty('isOpen') && !changes.isOpen) {
            // in multi selection
            if (this.isArrayValueMode()) {
                return {
                    ...changes,
                    inputValue: undefined,
                };
            }
        }

        return changes;
    };

    handleStateChange = (
        changes: StateChangeOptions<DropdownOption>,
        downshift: ControllerStateAndHelpers<DropdownOption>
    ) => {
        const { onClose, onOpen } = this.props;

        // when user input changed
        if (changes.hasOwnProperty('inputValue')) {
            const inputValue = changes.inputValue ?? '';
            const isUserTyping = changes.type === Downshift.stateChangeTypes.changeInput;
            const isItemSelection =
                changes.type === Downshift.stateChangeTypes.clickItem ||
                changes.type === Downshift.stateChangeTypes.keyDownEnter;

            this.props.onInputChange?.(inputValue, { fromItemSelection: isItemSelection });

            // In multi-select mode, skip updating visible options when:
            // 1. Change is from item selection (click/Enter), or
            // 2. Empty inputValue not from user typing (preserve current filtering)
            const shouldSkipUpdate =
                this.isMulti() && (isItemSelection || (inputValue === '' && !isUserTyping));

            if (!shouldSkipUpdate) {
                this.updateVisibleOptions(changes.inputValue);
            }
        }

        // when dropdown is opened
        if (changes.hasOwnProperty('isOpen') && changes.isOpen) {
            onOpen?.();
            this.resetVisibleOptions();
        }

        // when dropdown is closed
        if (changes.hasOwnProperty('isOpen') && !changes.isOpen) {
            this.setState({ multiItemSelectionStartIndex: null });
            onClose?.();
        }

        // handle scrolling when highlightedIndex has changed
        // TODO: not working with current virtualization library, implement with a different library
        // if (changes.hasOwnProperty('highlightedIndex') && changes.highlightedIndex != null) {
        // this.scrollToItem(change.highlightedIndex);
        // }
    };

    handleFocus = (event, downshift: ControllerStateAndHelpers<DropdownOption>) => {
        downshift.openMenu();
        this.inputRef.current?.select();
    };

    checkIsSelectedItemChanged = (prevItem, item) => {
        return !isEqual(prevItem, item);
    };

    handleCollapseButtonClick = (e) => {
        const { isExpanded } = this.state;
        e.preventDefault();
        e.stopPropagation();
        const wasExpanded = isExpanded;
        this.setState({ isExpanded: !isExpanded }, () => {
            if (wasExpanded) {
                // Double RAF to ensure scroll happens after layout calculations
                this.safeRequestAnimationFrame(() => {
                    this.safeRequestAnimationFrame(() => {
                        const scrollTarget = this.inputRef.current || this.rootRef.current;
                        if (scrollTarget) {
                            const rect = scrollTarget.getBoundingClientRect();
                            if (rect.top < 0) {
                                scrollTarget.scrollIntoView({
                                    behavior: 'auto',
                                    block: 'start',
                                });
                            }
                        }
                    });
                });
            }
        });
    };

    render() {
        const { onChange, onSelect, options, value, ...restProps } = this.props;
        const { visibleOptions } = this.state;

        return (
            <Downshift
                itemToString={this.handleValueToLabel}
                itemCount={visibleOptions.length}
                onStateChange={this.handleStateChange}
                onSelect={this.handleItemSelect}
                selectedItem={value ?? null}
                selectedItemChanged={this.checkIsSelectedItemChanged}
                stateReducer={this.stateReducer}
                initialInputValue=""
                {...restProps}
            >
                {this.renderDropdown}
            </Downshift>
        );
    }

    setRootRef = (downshiftRootProps: any) => (node: HTMLDivElement) => {
        this.rootRef.current = node;
        if (typeof downshiftRootProps.ref === 'function') {
            downshiftRootProps.ref(node);
        } else if (downshiftRootProps.ref && 'current' in downshiftRootProps.ref) {
            downshiftRootProps.ref.current = node;
        }
    };

    renderMenuHeader = (downshift): ReactNode => {
        const { renderMenuHeader } = this.props;
        const innerState = this.getInnerState(downshift);

        if (renderMenuHeader == null) {
            return null;
        }

        return renderMenuHeader(innerState);
    };

    renderDropdown = (downshift: ControllerStateAndHelpers<DropdownOption>) => {
        const {
            autoFocus,
            status,
            size,
            color,
            isClearable,
            isSearchable,
            placeholder,
            disabled,
            name,
            renderOption,
            renderTopOption,
            renderSelectedOption,
            optionsLabel,
            topOptionsLabel,
            multiselectLabel,
            style,
            id,
            dataTestid,
            valueMode,
            optionValueGetter,
            optionLabelGetter,
            optionDisabledGetter,
            optionSearchTermGetter,
            optionHiddenGetter,
            optionHeightGetter,
            inputFieldStyle,
            isSecondary,
            label,
            menuStyle,
            inputFieldContainerStyle,
            innerInputFieldStyle,
            selectedOptionStyle,
            getSelectedOptionContainerStyle,
            className,
            isCollapsible,
            options,
        } = this.props;
        const {
            visibleOptions: rawVisibleOptions,
            topOptions: rawTopOptions,
            menuPosition,
            multiItemSelectionStartIndex,
            isExpanded,
            canCollapse,
            lastVisibleRowBottom,
            overlayTopPx,
            overlayHeightPx,
        } = this.state;
        const flatOptions = this.flattenOptionsProp(options);
        const itemHeight = getMenuMetrics(size).itemHeight;

        const removeHiddenOptions = (option) => {
            return !optionHiddenGetter(option);
        };

        const visibleOptions = rawVisibleOptions.filter(removeHiddenOptions);
        const topOptions = rawTopOptions.filter(removeHiddenOptions);
        const optionsListHeight = visibleOptions.reduce(
            (height, option) => height + (optionHeightGetter?.(option) ?? itemHeight),
            0
        );

        const optionGetters = {
            optionValueGetter,
            optionLabelGetter,
            optionDisabledGetter,
            optionSearchTermGetter: optionSearchTermGetter || optionLabelGetter,
        };

        const {
            getRootProps,
            getInputProps,
            getToggleButtonProps,
            getItemProps,
            isOpen,
            toggleMenu,
            selectedItem: downshiftValue,
            highlightedIndex,
            inputValue = '',
        } = downshift;

        // get selected options from options by matching value
        let selectedOptions;
        switch (valueMode) {
            case 'single':
                selectedOptions =
                    downshiftValue == null
                        ? []
                        : flatOptions.filter((o) => optionValueGetter(o) === downshiftValue);
                break;

            case 'singleArray':
            case 'multiArray':
                selectedOptions =
                    downshiftValue == null
                        ? []
                        : flatOptions.filter((o) => downshiftValue.includes(optionValueGetter(o)));
                break;

            default:
                break;
        }

        const isOptionSelected = (option) =>
            selectedOptions.find(
                (selectedOption) => optionValueGetter(selectedOption) === optionValueGetter(option)
            ) !== undefined;

        const isMenuOnTop = menuPosition === 'top';
        const isSelectedOptionRemovable =
            this.isMulti() && (isClearable || selectedOptions.length > 1);

        const isCollapsibleMode = !!isCollapsible && isSearchable && valueMode === 'multiArray';

        const showCollapse = isCollapsibleMode && !!canCollapse;

        const collapsedMaxHeight = lastVisibleRowBottom;
        const rootProps = getRootProps({}, { suppressRefError: true });

        return (
            <RootContainer
                {...rootProps}
                ref={this.setRootRef(rootProps)}
                style={style}
                tabIndex="0"
                className={className}
                size={size}
            >
                <InputContainer>
                    <InputFieldContainer
                        id={id}
                        data-testid={dataTestid}
                        as="div"
                        {...{
                            isSecondary,
                            size,
                            status,
                            disabled,
                            color,
                            isSearchable,
                            valueMode,
                            inputFieldStyle,
                            onClick: (ev) =>
                                isSearchable ? this.handleFocus(ev, downshift) : toggleMenu(ev),
                            overlayTopPx,
                            overlayHeightPx,
                            showCollapsedOverlay:
                                showCollapse && !isExpanded && overlayTopPx != null,
                            clipStickyOverflow: showCollapse && isExpanded,
                        }}
                        style={inputFieldContainerStyle}
                        innerInputStyle={innerInputFieldStyle}
                    >
                        {isSearchable ? (
                            showCollapse ? (
                                <SearchRow isSticky={showCollapse && isExpanded}>
                                    <InputField
                                        {...getInputProps({
                                            ref: this.inputRef,
                                            size: placeholder != null ? placeholder.length : 20,
                                            isOpen,
                                            autoFocus,
                                            disabled,
                                            placeholder,
                                            name,
                                            value:
                                                inputValue && inputValue.trimStart
                                                    ? inputValue.trimStart()
                                                    : inputValue || '',
                                            readOnly: !isSearchable,
                                        })}
                                        style={{ width: getPlaceholderInputWidth(placeholder) }}
                                        size={size}
                                        placeholder={placeholder}
                                    />
                                    <CollapseButton
                                        onClick={this.handleCollapseButtonClick}
                                        isSecondary={isSecondary}
                                        isExpanded={isExpanded}
                                    >
                                        {isExpanded
                                            ? _('show_less_options')
                                            : _('show_all_options', {
                                                  total: selectedOptions.length,
                                              })}
                                    </CollapseButton>
                                </SearchRow>
                            ) : (
                                <InputField
                                    {...getInputProps({
                                        ref: this.inputRef,
                                        size: placeholder != null ? placeholder.length : 20,
                                        isOpen,
                                        autoFocus,
                                        disabled,
                                        placeholder,
                                        name,
                                        value:
                                            inputValue && inputValue.trimStart
                                                ? inputValue.trimStart()
                                                : inputValue || '',
                                        readOnly: !isSearchable,
                                    })}
                                    size={size}
                                />
                            )
                        ) : isEmpty(selectedOptions) ? (
                            <SelectedOptionContainer
                                isPlaceholder={!label}
                                size={size}
                                valueMode={valueMode}
                            >
                                {label || placeholder}
                            </SelectedOptionContainer>
                        ) : (
                            ''
                        )}
                        {isCollapsibleMode ? (
                            <SelectedItemsWrapper
                                ref={this.selectedWrapperRef}
                                isCollapsed={showCollapse && !isExpanded}
                                maxHeightPx={
                                    showCollapse && !isExpanded ? collapsedMaxHeight : null
                                }
                                canCollapse={canCollapse}
                            >
                                {selectedOptions.map((option) => (
                                    <SelectedOptionContainer
                                        key={optionValueGetter(option)}
                                        valueMode={valueMode}
                                        disabled={optionDisabledGetter(option)}
                                        data-selected-chip
                                        size={size}
                                        style={getSelectedOptionContainerStyle?.(option)}
                                    >
                                        <SelectedOption style={selectedOptionStyle}>
                                            {optionLabelGetter(option)}
                                            {isSelectedOptionRemovable && (
                                                <SelectedOptionRemoveButton
                                                    {...this.getRemoveButtonProps(
                                                        { item: option },
                                                        downshift
                                                    )}
                                                >
                                                    <StyledClearIcon />
                                                </SelectedOptionRemoveButton>
                                            )}
                                        </SelectedOption>
                                    </SelectedOptionContainer>
                                ))}
                                {showCollapse && !isExpanded && overlayTopPx != null && <></>}
                            </SelectedItemsWrapper>
                        ) : (
                            selectedOptions.map((option) => (
                                <SelectedOptionContainer
                                    key={optionValueGetter(option)}
                                    valueMode={valueMode}
                                    disabled={optionDisabledGetter(option)}
                                    size={size}
                                    style={getSelectedOptionContainerStyle?.(option)}
                                >
                                    <SelectedOption style={selectedOptionStyle}>
                                        {valueMode === 'single'
                                            ? (renderSelectedOption?.(option) ??
                                              renderTruncatableLabel(
                                                  optionLabelGetter(option),
                                                  isOpen
                                              ))
                                            : optionLabelGetter(option)}
                                        {isSelectedOptionRemovable && (
                                            <SelectedOptionRemoveButton
                                                {...this.getRemoveButtonProps(
                                                    { item: option },
                                                    downshift
                                                )}
                                            >
                                                <StyledClearIcon />
                                            </SelectedOptionRemoveButton>
                                        )}
                                    </SelectedOption>
                                </SelectedOptionContainer>
                            ))
                        )}
                        {isClearable && !isEmpty(selectedOptions) ? (
                            <ActionButton
                                color={color}
                                onClick={(ev) => {
                                    ev.preventDefault();
                                    this.handleClearButtonClick(downshift);
                                }}
                                aria-label="clear selection"
                                disabled={disabled}
                                isSecondary={isSecondary}
                            >
                                <StyledClearIcon />
                            </ActionButton>
                        ) : (
                            <ActionButton
                                {...getToggleButtonProps({
                                    disabled,
                                    color,
                                })}
                                style={{ pointerEvents: 'none' }}
                                isSecondary={isSecondary}
                            >
                                <SvgIcon
                                    alt="Down Icon"
                                    src={
                                        isOpen
                                            ? '/icons/ic-arrow-up.svg'
                                            : '/icons/ic-arrow-down.svg'
                                    }
                                    data-testid="dropdown-down-chevron"
                                />
                            </ActionButton>
                        )}
                    </InputFieldContainer>
                </InputContainer>
                <MenuContainer
                    isOpen={isOpen}
                    isOnTop={isMenuOnTop}
                    size={size}
                    innerRef={this.menuRef}
                    style={menuStyle}
                >
                    {isOpen && !!inputValue && this.renderMenuHeader(downshift)}
                    {!isOpen || isEmpty(topOptions) ? null : (
                        <TopOptionList>
                            {topOptionsLabel && (
                                <TopOptionFlexRow justify="space-between">
                                    <TopOptionLabel size={size}>{topOptionsLabel}</TopOptionLabel>
                                </TopOptionFlexRow>
                            )}
                            {topOptions
                                .map((topOption) => {
                                    const visibleOptionIndex = visibleOptions.findIndex(
                                        (vo) =>
                                            optionValueGetter(vo) === optionValueGetter(topOption)
                                    );
                                    return [topOption, visibleOptionIndex];
                                })
                                .map(([topOption, index]) => (
                                    <OptionListItem
                                        className="dropdown-option"
                                        key={`top_${optionValueGetter(topOption)}`}
                                        style={{ padding: '12px 20px 0 40px' }}
                                        size={size}
                                        {...getItemProps({
                                            item: topOption,
                                            index,
                                            disabled: optionDisabledGetter(topOption),
                                            isSelected: isOptionSelected(topOption),
                                            isHighlighted: highlightedIndex === index,
                                        })}
                                    >
                                        {renderTopOption
                                            ? renderTopOption(topOption, optionGetters)
                                            : this.defaultRenderOption(topOption, optionGetters)}
                                    </OptionListItem>
                                ))}
                        </TopOptionList>
                    )}

                    {!isOpen ? null : isEmpty(visibleOptions) ? (
                        <OptionListItem className="dropdown-option" size={size} disabled>
                            {'No options'}
                        </OptionListItem>
                    ) : (
                        <>
                            {(optionsLabel || multiselectLabel) && (
                                <TopOptionFlexRow justify="space-between">
                                    {optionsLabel && (
                                        <TopOptionLabel size={size}>{optionsLabel}</TopOptionLabel>
                                    )}
                                    {multiselectLabel && (
                                        <TopOptionLabel size={size}>
                                            {multiselectLabel}
                                        </TopOptionLabel>
                                    )}
                                </TopOptionFlexRow>
                            )}
                            <Virtuoso
                                style={{
                                    height:
                                        optionsListHeight > MAX_HEIGHT
                                            ? MAX_HEIGHT
                                            : optionsListHeight,
                                }}
                                data={visibleOptions}
                                itemContent={(index, option) => {
                                    const { onMultiSelect, name } = this.props;

                                    const isSelected = isOptionSelected(option);

                                    const handleMultiClick = (event) => {
                                        const hotkeyPressed = !!event[MULTISELECT_HOTKEY];

                                        const hasMultiSelectStart =
                                            multiItemSelectionStartIndex != null;
                                        const shouldStartMultiSelect =
                                            !isSelected && (!hotkeyPressed || !hasMultiSelectStart);
                                        const shouldClearMultiSelect =
                                            isSelected || (hotkeyPressed && hasMultiSelectStart);

                                        if (shouldStartMultiSelect) {
                                            this.setState({ multiItemSelectionStartIndex: index });

                                            if (hotkeyPressed && !hasMultiSelectStart) {
                                                const ev = {
                                                    target: {
                                                        name,
                                                        value: selectedOptions
                                                            .concat([option])
                                                            .filter(
                                                                (option) =>
                                                                    !optionDisabledGetter?.(option)
                                                            )
                                                            .map(optionValueGetter),
                                                    },
                                                };
                                                const innerState = this.getInnerState(downshift);
                                                onMultiSelect?.(ev, innerState);
                                            }
                                        } else if (shouldClearMultiSelect) {
                                            this.setState({ multiItemSelectionStartIndex: null });
                                        }

                                        if (hotkeyPressed && hasMultiSelectStart) {
                                            // add selected items from multiItemSelectionStartIndex to index
                                            const multiSelectedOptions =
                                                multiItemSelectionStartIndex >= index
                                                    ? visibleOptions.slice(
                                                          index,
                                                          multiItemSelectionStartIndex + 1
                                                      )
                                                    : visibleOptions.slice(
                                                          multiItemSelectionStartIndex,
                                                          index + 1
                                                      );

                                            const unselectedOptions = multiSelectedOptions.filter(
                                                (option) =>
                                                    !selectedOptions
                                                        .map(optionValueGetter)
                                                        .includes(optionValueGetter(option))
                                            );

                                            if (unselectedOptions.length) {
                                                const ev = {
                                                    target: {
                                                        name: name,
                                                        value: selectedOptions
                                                            .concat(unselectedOptions)
                                                            .filter(
                                                                (option) =>
                                                                    !optionDisabledGetter?.(option)
                                                            )
                                                            .map(optionValueGetter),
                                                    },
                                                };
                                                const innerState = this.getInnerState(downshift);
                                                onMultiSelect?.(ev, innerState);
                                            }

                                            this.setState({
                                                multiItemSelectionStartIndex: index,
                                            });
                                        }
                                    };

                                    return (
                                        <div
                                            {...{
                                                // ref,
                                                style,
                                                disabled: optionDisabledGetter(option),
                                            }}
                                        >
                                            <OptionListItem
                                                className="dropdown-option"
                                                size={size}
                                                {...getItemProps({
                                                    item: option,
                                                    index,
                                                    disabled: optionDisabledGetter(option),
                                                    isSelected: isSelected,
                                                    isHighlighted: highlightedIndex === index,
                                                    isMultiSelectionHighlighted:
                                                        multiItemSelectionStartIndex === index,
                                                    onClick: this.isMulti()
                                                        ? handleMultiClick
                                                        : undefined,
                                                })}
                                            >
                                                {renderOption
                                                    ? renderOption(option, optionGetters)
                                                    : this.defaultRenderOption(
                                                          option,
                                                          optionGetters
                                                      )}
                                            </OptionListItem>
                                        </div>
                                    );
                                }}
                            />
                        </>
                    )}
                </MenuContainer>
            </RootContainer>
        );
    };
}

export default Dropdown;
